import { useQuery, useMutation } from '@tanstack/react-query';
import {
  GET_EMPLOYEE_DETAILS,
  SET_EMPLOYEE_DETAILS,
  UPDATE_MOBILE_NUMBER,
  VALIDATE_MOBILE_NUMBER,
} from '@/constants/API';
import { axios } from '@/lib/axios';
import { ExtractFnReturnType, queryClient, QueryConfig } from '@/lib/react-query';
import { UserData, EmployeeDetails } from '@/modules/auth';
import { API_RESPONSE } from '@/types/api';
import { serializeObject } from '@/utils/object';
import storage from '@/utils/storage';

export const getCurrentUser = async () => {
  let params = {};
  const currentLocation = storage.getItem('currentLocation');
  if (currentLocation) {
    params = { location_id: currentLocation.id };
  }
  try {
    return await axios.get<UserData>(`current_user/?${serializeObject(params)}`);
  } catch (error) {
    return null;
  }
};

export const getEmployeeDetails = async () => {
  const data = await axios.get<API_RESPONSE<EmployeeDetails>>(GET_EMPLOYEE_DETAILS);
  return data.data;
};

export const setEmployeeDetails = async (payload: EmployeeDetails) =>
  await axios.post<any>(SET_EMPLOYEE_DETAILS, payload);

type GetUserQueryFnType = typeof getCurrentUser;
type GetEmployeeDetailsQueryFnType = typeof getEmployeeDetails;

type UseUserOptions = {
  config?: QueryConfig<GetUserQueryFnType>;
};
export const useCurrentUser = ({ config }: UseUserOptions) =>
  useQuery<ExtractFnReturnType<GetUserQueryFnType>>({
    queryKey: ['current_user'],
    queryFn: async () => await getCurrentUser(),
    ...config,
  });

export const useEmployeeDetails = () =>
  useQuery<ExtractFnReturnType<GetEmployeeDetailsQueryFnType>>({
    queryKey: ['employee_details'],
    queryFn: async () => await getEmployeeDetails(),
  });

export const useUpdateEmployeeDetails = () =>
  useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries(['employee_details']);
    },
    mutationFn: setEmployeeDetails,
  });

type UpdateMobileNumber = {
  new_mobile_number: string;
};
type VeirfyUpdateMobileNumber = {
  new_mobile_number: string;
  otp: number;
};

export const updateMobileNumber = async (payload: UpdateMobileNumber) =>
  await axios.post<{
    success: boolean;
    message: string;
    mobile_number_verified: boolean;
  }>(UPDATE_MOBILE_NUMBER, payload);

export const useUpdateMobileNumber = () =>
  useMutation({
    mutationFn: updateMobileNumber,
  });

export const verifyUpdateMobileNumberOTP = async (payload: VeirfyUpdateMobileNumber) =>
  await axios.put<{
    success: boolean;
    message: string;
    mobile_number_verified: boolean;
  }>(VALIDATE_MOBILE_NUMBER, payload);

export const useVerifyUpdateMobileNumberOTP = () =>
  useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries(['walletList']);
    },
    mutationFn: verifyUpdateMobileNumberOTP,
  });
